import { displayVessel, shortPort } from './shipChanges.js';
import { formatEtaDate, predictedEta, timelinePct, toDayMs } from './shipmentSchedule.js';
import { stopCountryLabel } from './stopLabel.js';
import { groupPinStacks } from './pinStacks.js';

/** Sea box sitting at a transshipment hub, waiting for its onward vessel. */
export function isHubWaiting(shipment) {
  return Boolean(
    shipment?.in_transshipment
    && shipment?.hub_port
    && (shipment?.hub_mode === 'port' || shipment?.hub_mode === 'rail'),
  );
}

/** Latest ShipsGo ocean vessel. Booking is separate; hub-waiting boxes have none. */
export function currentVesselName(shipment) {
  if (isHubWaiting(shipment)) return '';
  return shipment?.current_vessel || shipment?.currentVessel || shipment?.vessel || '';
}

export function hubPortName(shipment) {
  return shortPort(shipment?.hub_port);
}

/** Short label for pin stacks, popups and lists. */
export function hubLabel(shipment) {
  return { key: 'seaTracking.card.hubAt', params: { port: hubPortName(shipment) } };
}

// Noon local time keeps a date-only value on the same calendar day in every timezone.
function displayDate(raw) {
  if (!raw) return null;
  const ymd = String(raw).slice(0, 10);
  return formatEtaDate(/^\d{4}-\d{2}-\d{2}$/.test(ymd) ? `${ymd}T12:00:00` : raw);
}

/** i18n lines for the hub card: [{ key, params }]. */
export function buildHubLines(shipment) {
  const lines = [{ key: 'seaTracking.card.hubStatus' }];
  const prev = shipment?.previous_vessel;
  if (prev) lines.push({ key: 'seaTracking.card.previousVessel', params: { vessel: displayVessel(prev) } });
  if (shipment?.next_vessel) {
    lines.push({ key: 'seaTracking.card.nextVesselNamed', params: { vessel: displayVessel(shipment.next_vessel) } });
  } else {
    lines.push({ key: 'seaTracking.card.nextVesselTba' });
  }
  const date = displayDate(shipment?.next_departure_est);
  if (date) {
    lines.push({
      key: shipment.departure_overdue ? 'seaTracking.card.estDepartureOverdue' : 'seaTracking.card.estDeparture',
      params: { date },
    });
  }
  return lines;
}

function boxCount(shipment) {
  const n = Number(shipment?.total_containers);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function departureDate(shipment) {
  const d = shipment?.next_departure_est;
  return d ? String(d).slice(0, 10) : null;
}

const upper = (v) => String(v ?? '').trim().toUpperCase();

/** Stable identity of a hub (independent of stack order); '' for non-hub cards. */
export function hubKeyOf(shipment) {
  return isHubWaiting(shipment) ? upper(shipment.hub_port) : '';
}

/** The stack item whose hub card belongs to the open Waiting-at-port panel, or null. */
export function hubFocusInStack(stack, hubPanel) {
  const key = hubPanel?.hubKey;
  if (!key || !Array.isArray(stack)) return null;
  return stack.find((item) => isHubWaiting(item?.shipment) && hubKeyOf(item.shipment) === key) || null;
}

/** One trip = one hub card (next vessel + leaving day). Independent of list order. */
export function tripKeyOf(card) {
  return [hubKeyOf(card), upper(card?.next_vessel) || 'TBA', departureDate(card) || '', upper(card?.container_number)].join('|');
}

/** voyage_stops arrives as an array or a JSON string. */
export function parseStops(raw) {
  if (Array.isArray(raw)) return raw.filter((s) => s && typeof s === 'object');
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => s && typeof s === 'object') : [];
  } catch {
    return [];
  }
}

const present = (v) => v !== null && v !== undefined && v !== '';
const dateOrNull = (v) => (present(v) ? String(v).slice(0, 10) : null);
const countOrNull = (v) => {
  if (!present(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Numeric 0 stays 0; missing stays null. Date window is only a fallback for missing. */
function stopProgress(stop, etd, eta) {
  if (present(stop?.transit_percentage)) {
    const n = Number(stop.transit_percentage);
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  return timelinePct(etd, eta);
}

const DASH_LABEL = '—';
const numOrNull = (v) => (present(v) && Number.isFinite(Number(v)) ? Number(v) : null);

/** A stop row can merge several shipments. Heads "agree" only when ETD day, predicted ETA day,
 *  delay and progress are all identical; otherwise one ETD/%/delay would be a fake combination. */
function headsSummary(stop) {
  const heads = Array.isArray(stop?.heads) ? stop.heads.filter((h) => h && typeof h === 'object') : [];
  if (heads.length <= 1) return null;
  const sig = (h) => [dateOrNull(h.etd), dateOrNull(predictedEta(h)), numOrNull(h.delay_days), numOrNull(h.transit_percentage)].join('|');
  // Normal row only when every head agrees AND the stop's own aggregate values equal that common signature.
  if (new Set([...heads.map(sig), sig(stop)]).size === 1) return null;
  const etds = heads.map((h) => dateOrNull(h.etd)).filter(Boolean).sort();
  const etas = heads.map((h) => dateOrNull(predictedEta(h))).filter(Boolean).sort();
  // One shipment can yield several heads: count shipment ids, else containers.
  const ids = new Set(heads.map((h) => (present(h.shipsgo_shipment_id) ? `s:${h.shipsgo_shipment_id}`
    : (present(h.container_number) ? `c:${h.container_number}` : null))).filter(Boolean));
  return {
    shipments: Math.max(1, ids.size),
    etdFrom: etds[0] || null,
    etdTo: etds[etds.length - 1] || null,
    etdIncomplete: etds.length < heads.length,
    // An incomplete ETA set is never shown as a confirmed "latest".
    latestEta: etas.length < heads.length ? null : etas[etas.length - 1],
  };
}

function stopRow(card, stop, key, fromCard) {
  const etd = dateOrNull(stop?.etd);
  const eta = dateOrNull(predictedEta(stop));
  const merged = headsSummary(stop);
  return {
    key,
    stop,
    fromCard,
    destination: stopCountryLabel({
      port: stop?.arrival_port,
      stopCountry: stop?.destination_country,
      cardPort: card?.arrival_port,
      cardCountry: card?.destination_country,
    }),
    boxes: countOrNull(stop?.total_containers),
    etd,
    etdLabel: displayDate(etd),
    eta,
    etaLabel: displayDate(eta),
    progress: merged ? null : stopProgress(stop, etd, eta),
    delay_days: merged ? null : (stop?.delay_days ?? null),
    ...(merged ? {
      merged: true,
      shipments: merged.shipments,
      etdFrom: merged.etdFrom,
      etdTo: merged.etdTo,
      latestEta: merged.latestEta,
      latestEtaLabel: displayDate(merged.latestEta),
      etdFromLabel: displayDate(merged.etdFrom),
      etdToLabel: merged.etdIncomplete ? DASH_LABEL : displayDate(merged.etdTo),
    } : {}),
  };
}

const dayKey = (d) => {
  const ms = toDayMs(d);
  return ms == null ? Number.POSITIVE_INFINITY : ms;
};

/** Stops of one trip, sorted on a COPY: origin ETD, predicted ETA, port; missing last. */
export function buildTripStops(card, tripKey) {
  const raw = parseStops(card?.voyage_stops).filter((s) => String(s?.arrival_port || '').trim());
  if (raw.length === 0) {
    // Card-backed row only: weights stay off it (nothing is known per stop).
    return [stopRow(card, {
      arrival_port: card?.arrival_port,
      destination_country: card?.destination_country,
      total_containers: card?.total_containers,
      etd: card?.etd,
      eta: card?.eta,
      estimated_arrival: card?.estimated_arrival,
      eta_updated: card?.eta_updated,
      eta_initial: card?.eta_initial,
      transit_percentage: card?.transit_percentage,
      delay_days: card?.delay_days,
    }, `${tripKey}#card`, true)];
  }
  // Stable identity: destination port + origin ETD day (+ occurrence number only for true duplicates),
  // so open state follows the stop when a refresh reorders the array.
  const seen = new Map();
  const rows = raw.map((s) => {
    const base = `${tripKey}#${upper(s.arrival_port)}|${dateOrNull(s.etd) || ''}`;
    const n = seen.get(base) || 0;
    seen.set(base, n + 1);
    return stopRow(card, s, n ? `${base}#${n}` : base, false);
  });
  return rows.sort((a, b) => (
    dayKey(a.merged ? a.etdFrom : a.etd) - dayKey(b.merged ? b.etdFrom : b.etd)
    || dayKey(a.merged ? a.latestEta : a.eta) - dayKey(b.merged ? b.latestEta : b.eta)
    || (a.destination && b.destination ? a.destination.localeCompare(b.destination) : Number(!a.destination) - Number(!b.destination))
  ));
}

/** Other hubs sharing the pin, one row each, so the user can switch hub. */
function groupHubs(cards) {
  const byKey = new Map();
  (Array.isArray(cards) ? cards : []).filter(isHubWaiting).forEach((card) => {
    const key = hubKeyOf(card);
    const g = byKey.get(key) || { hubKey: key, port: hubPortName(card), boxes: 0 };
    g.boxes += boxCount(card);
    byKey.set(key, g);
  });
  return [...byKey.values()].sort((a, b) => a.port.localeCompare(b.port));
}

/**
 * Everything waiting at one hub: trips (one per hub card) with their stops attached.
 * Trips ascend by next departure (undated last); overdue is only a badge.
 */
export function buildHubWaitingPanel(hubCards, others = [], otherHubCards = []) {
  const cards = (Array.isArray(hubCards) ? hubCards : []).filter(isHubWaiting);
  const trips = cards.map((card) => {
    const key = tripKeyOf(card);
    const departure = departureDate(card);
    return {
      key,
      shipment: card,
      vessel: card.next_vessel ? displayVessel(card.next_vessel) : null,
      departure,
      departureLabel: displayDate(departure),
      overdue: Boolean(card.departure_overdue),
      boxes: boxCount(card),
      stops: buildTripStops(card, key),
    };
  });
  trips.sort((a, b) => (
    (a.departure && b.departure ? a.departure.localeCompare(b.departure) : Number(!a.departure) - Number(!b.departure))
    || (a.vessel || '\uffff').localeCompare(b.vessel || '\uffff')
    || b.boxes - a.boxes
    || a.key.localeCompare(b.key)
  ));
  const dates = trips.map((x) => x.departure).filter(Boolean).sort();
  return {
    hubKey: cards.length ? hubKeyOf(cards[0]) : '',
    port: cards.length ? hubPortName(cards[0]) : '',
    totalBoxes: trips.reduce((sum, x) => sum + x.boxes, 0),
    tripCount: trips.length,
    earliestDeparture: dates[0] || null,
    earliestDepartureLabel: displayDate(dates[0]),
    overdueCount: trips.filter((x) => x.overdue).length,
    trips,
    others: Array.isArray(others) ? others : [],
    otherHubs: groupHubs(otherHubCards),
  };
}

/**
 * Live members of an open panel, derived from the CURRENT filtered list so filters,
 * Sea off and refreshes apply. Membership is the map's own connected stack
 * (groupPinStacks), so a panel sees what the pin the user clicked contained.
 */
export function selectHubPanelMembers({ shipments, hubKey, predicate, pinLatLng, cellKey, km }) {
  const ok = typeof predicate === 'function' ? predicate : () => true;
  const visible = (shipments || []).filter((s) => s && ok(s));
  const hubCards = visible.filter((s) => isHubWaiting(s) && hubKeyOf(s) === hubKey);
  const others = [];
  const otherHubCards = [];
  groupPinStacks(visible, pinLatLng, cellKey, km).forEach(({ items }) => {
    if (!items.some((i) => hubCards.includes(i.shipment))) return;
    items.forEach(({ shipment }) => {
      if (!isHubWaiting(shipment)) others.push(shipment);
      else if (hubKeyOf(shipment) !== hubKey) otherHubCards.push(shipment);
    });
  });
  return { hubCards, others, otherHubCards };
}

/** Hub card that holds this container (lead container_number or any all_containers member). */
export function findHubCardByContainer(hubCards, containerNumber) {
  const id = upper(containerNumber);
  if (!id) return null;
  return (hubCards || []).find((c) => {
    if (upper(c?.container_number) === id) return true;
    const all = c?.all_containers;
    const tokens = Array.isArray(all) ? all.map(upper) : upper(all).split(/[^A-Z0-9]+/);
    return tokens.includes(id);
  }) || null;
}

const panelFor = (card, focus) => ({
  kind: 'panel', hubKey: hubKeyOf(card), focusTripKey: focus ? tripKeyOf(card) : null,
});

/** Map pin click. Hub members win over `stackSize > 1`, so even one hub card opens the panel. */
export function resolvePinHit(stack) {
  const items = Array.isArray(stack) ? stack : [];
  const hubItem = items.find((i) => isHubWaiting(i?.shipment));
  if (hubItem) return panelFor(hubItem.shipment, false);
  if (items.length > 1) return { kind: 'picker', stack: items };
  return { kind: 'card', shipment: items[0]?.shipment || null };
}

/** Container search result. A hub box never becomes a ShipmentCard; filtered-out ones do nothing. */
export function resolveSearchHit({ result, fromActive, shipments, predicate, enabled = true }) {
  if (!enabled || !result) return { kind: 'none' };
  const ok = typeof predicate === 'function' ? predicate : () => true;
  const hubCards = (shipments || []).filter((s) => isHubWaiting(s) && ok(s));
  const card = findHubCardByContainer(hubCards, result.container_number)
    || (fromActive && findHubCardByContainer(hubCards, fromActive.container_number));
  if (card) return panelFor(card, true);
  if (isHubWaiting(result) || isHubWaiting(fromActive)) return { kind: 'none' };
  return { kind: 'card', shipment: fromActive ? { ...result, ...fromActive } : result };
}

/** Prop-driven selection: a hub card becomes the panel (or is cleared), anything else is left alone. */
export function resolveSelectedProp({ selected, shipments, predicate, enabled = true }) {
  if (!selected || !isHubWaiting(selected)) return { kind: 'none' };
  const ok = typeof predicate === 'function' ? predicate : () => true;
  const hubCards = enabled ? (shipments || []).filter((s) => isHubWaiting(s) && ok(s)) : [];
  const card = findHubCardByContainer(hubCards, selected.container_number);
  return card ? panelFor(card, true) : { kind: 'clear' };
}

/**
 * Selection transitions as data. state = { hubPanel, stackPick, hoveredStackId, selectedShipment };
 * opening a panel clears the card, picker and hover, and bumps `seq` so a repeated search re-expands.
 */
export function applySelectionDecision(state, d) {
  switch (d.kind) {
    case 'panel':
      return {
        hubPanel: { hubKey: d.hubKey, focusTripKey: d.focusTripKey || null, seq: (state.hubPanel?.seq || 0) + 1 },
        stackPick: null, hoveredStackId: null, selectedShipment: null,
        clearSearch: true, // the search detail panel must not stay over the waiting panel
      };
    case 'picker':
      return { ...state, hubPanel: null, stackPick: d.stack, hoveredStackId: d.stack[0]?.shipment?.container_number || null };
    case 'card': // search results keep the picker and hover; a single-pin click keeps only the hover (as before the panel existed)
      return {
        ...state, hubPanel: null,
        stackPick: d.keepPicker ? state.stackPick : null,
        hoveredStackId: d.keepPicker || d.keepHover ? state.hoveredStackId : null,
        selectedShipment: d.shipment,
      };
    case 'clear':
      return { ...state, selectedShipment: null };
    case 'miss':
      return { hubPanel: null, stackPick: null, hoveredStackId: null, selectedShipment: null };
    default:
      return state;
  }
}
